import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  getAvailability,
  getFiberLink,
  getStateEvents,
  getStatusHistory,
  getThroughput,
  syncFiberLink,
  type SyncFiberLinkResult,
} from '@/api/fiberMonitoringApi'
import type {
  FiberLink,
  FiberLinkAvailability,
  FiberLinkEventsResponse,
  LinkRange,
  StateHistory,
  Throughput,
} from '@/types/fiberMonitoring'

export const useFiberMonitoringStore = defineStore('fiberMonitoring', () => {
  const link = ref<FiberLink | null>(null)
  const statusHistory = ref<StateHistory | null>(null)
  const throughput = ref<Throughput | null>(null)
  const events = ref<FiberLinkEventsResponse | null>(null)
  const availability = ref<FiberLinkAvailability | null>(null)
  const range = ref<LinkRange>('1d')

  const loadingLink = ref(false)
  const loadingCharts = ref(false)
  const linkError = ref<string | null>(null)
  const chartsError = ref<string | null>(null)

  async function loadLink(codeContrat: string) {
    loadingLink.value = true
    linkError.value = null
    link.value = null
    try {
      link.value = await getFiberLink(codeContrat)
    } catch (err) {
      linkError.value = err instanceof Error ? err.message : 'Erreur de chargement du lien'
    } finally {
      loadingLink.value = false
    }
  }

  async function loadCharts(codeContrat: string) {
    loadingCharts.value = true
    chartsError.value = null
    try {
      const [history, tp, ev, av] = await Promise.all([
        getStatusHistory(codeContrat, range.value),
        getThroughput(codeContrat, range.value),
        getStateEvents(codeContrat, range.value),
        getAvailability(codeContrat, range.value),
      ])
      statusHistory.value = history
      throughput.value = tp
      events.value = ev
      availability.value = av
    } catch (err) {
      chartsError.value = err instanceof Error ? err.message : 'Erreur de chargement des données'
    } finally {
      loadingCharts.value = false
    }
  }

  async function load(codeContrat: string) {
    await Promise.all([loadLink(codeContrat), loadCharts(codeContrat)])
  }

  async function setRange(codeContrat: string, r: LinkRange) {
    if (range.value === r) return
    range.value = r
    await loadCharts(codeContrat)
  }

  const syncing = ref(false)
  const lastSyncResult = ref<SyncFiberLinkResult | null>(null)

  async function syncLink(codeContrat: string, days = 30) {
    syncing.value = true
    try {
      lastSyncResult.value = await syncFiberLink(codeContrat, days)
      // Recharge l'état + les graphes après la synchro.
      await load(codeContrat)
    } finally {
      syncing.value = false
    }
  }

  function reset() {
    link.value = null
    statusHistory.value = null
    throughput.value = null
    events.value = null
    availability.value = null
    linkError.value = null
    chartsError.value = null
  }

  return {
    link,
    statusHistory,
    throughput,
    events,
    availability,
    range,
    loadingLink,
    loadingCharts,
    linkError,
    chartsError,
    syncing,
    lastSyncResult,
    loadLink,
    loadCharts,
    load,
    setRange,
    syncLink,
    reset,
  }
})
